import { match } from 'ts-pattern'

import { ListBlockConfigV3, QuoteBlock } from '@sequencehq/quote-content'

import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { bulletListAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/bulletList.adapter.v1'
import { bulletListAdapterV2 } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/bulletList.adapter.v2'
import { heading1Adapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/heading1.adapter.v1'
import { heading2Adapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/heading2.adapter.v1'
import { heading3Adapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/heading3.adapter.v1'
import { imageAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/image.adapter.v1'
import { numberListAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/numberList.adapter.v1'
import { numberListAdapterV2 } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/numberList.adapter.v2'
import { paragraphAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/paragraph.adapter.v1'
import { phasesAdapter } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/phases.adapter.v1'
import { blocknoteSchema } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/blocknoteSchema'
import { dividerAdapter } from './divider.adapter.v1'
import { tableAdapter } from './table.adapter.v1'
import { BlockAdapter } from './blockAdapter.types'
import { textContentAdapter } from './utils/textContent.adapter'

/**
 * Since these adapters are called recursively, we can run into circular dependency errors
 * right now if they import this file! To avoid that, we define them in this file.
 */
export const bulletListAdapterV3: BlockAdapter<'bulletListItem'> = {
  in: block => {
    const config = block.config as ListBlockConfigV3
    const content = config.items[0]

    /**
     * Within the scope of the v3 configuration 'shape' we no longer group
     * the items into a single block, as this dramatically simplifies the
     * modelling for nested lists. However, to retain backwards compatability
     * we need to handle the situation where the items array had multiple entries.
     */
    if (config.items.length > 1) {
      return config.items.map(item => ({
        type: 'bulletListItem',
        children: [],
        content: textContentAdapter.in(item.text),
        props: {
          backgroundColor: item.backgroundColor ?? 'default',
          textColor: item.textColor ?? 'default',
          textAlignment: item.textAlignment ?? 'left'
        }
      })) as ReturnType<BlockAdapter['in']>
    }

    return [
      {
        type: 'bulletListItem',
        children: (config.children ?? []).flatMap(childBlock => {
          if (!childBlock || childBlock.blockType === 'table') {
            return []
          }
          const adapter = blockAdapters.in[childBlock.blockType]
          if (!adapter) {
            throw new Error(
              `No block adapter found for block type: ${childBlock.blockType}`
            )
          }
          return adapter(childBlock)
        }),
        content: content ? textContentAdapter.in(content.text) : undefined,
        props: content
          ? {
              backgroundColor: content.backgroundColor ?? 'default',
              textColor: content.textColor ?? 'default',
              textAlignment: content.textAlignment ?? 'left'
            }
          : undefined
      }
    ] as ReturnType<BlockAdapter['in']>
  },
  out: ctx => block => {
    const items = block.content
      ? [
          {
            text: textContentAdapter.out(block.content),
            backgroundColor: block.props.backgroundColor,
            textColor: block.props.textColor,
            textAlignment: block.props.textAlignment
          }
        ]
      : ([] as ListBlockConfigV3['items'])
    const children = block.children.map(childBlock => {
      const adapter = blockAdapters.out[childBlock.type]
      if (!adapter) {
        throw new Error(
          `No block adapter found for block type: ${childBlock.type}`
        )
      }
      return adapter(ctx)(childBlock)
    })

    return {
      blockType: 'bulletList',
      config: { items, children },
      version: 3
    }
  }
}

export const numberListAdapterV3: BlockAdapter<'numberedListItem'> = {
  in: block => {
    const config = block.config as ListBlockConfigV3
    const content = config.items[0]

    /**
     * Within the scope of the v3 configuration 'shape' we no longer group
     * the items into a single block, as this dramatically simplifies the
     * modelling for nested lists. However, to retain backwards compatability
     * we need to handle the situation where the items array had multiple entries.
     */
    if (config.items.length > 1) {
      return config.items.map((item, idx) => ({
        type: 'numberedListItem',
        children: [],
        content: textContentAdapter.in(item.text),
        props: {
          backgroundColor: item.backgroundColor ?? 'default',
          textColor: item.textColor ?? 'default',
          textAlignment: item.textAlignment ?? 'left',
          start: (config.startsFrom ?? 1) + idx
        }
      })) as ReturnType<BlockAdapter['in']>
    }

    return [
      {
        type: 'numberedListItem',
        children: (config.children ?? []).flatMap(childBlock => {
          const adapter = blockAdapters.in[childBlock.blockType]
          if (!adapter) {
            throw new Error(
              `No block adapter found for block type: ${childBlock.blockType}`
            )
          }
          return adapter(childBlock)
        }),
        content: textContentAdapter.in(content.text),
        props: {
          backgroundColor: content.backgroundColor ?? 'default',
          textColor: content.textColor ?? 'default',
          textAlignment: content.textAlignment ?? 'left',
          start: config.startsFrom
        }
      }
    ] as ReturnType<BlockAdapter['in']>
  },
  out: ctx => block => {
    const items = block.content
      ? [
          {
            text: textContentAdapter.out(block.content),
            backgroundColor: block.props.backgroundColor,
            textColor: block.props.textColor,
            textAlignment: block.props.textAlignment
          }
        ]
      : ([] as ListBlockConfigV3['items'])
    const children = block.children.map(childBlock => {
      const adapter = blockAdapters.out[childBlock.type]
      if (!adapter) {
        throw new Error(
          `No block adapter found for block type: ${childBlock.type}`
        )
      }
      return adapter(ctx)(childBlock)
    })

    return {
      blockType: 'numberList',
      config: { items, children, startsFrom: block.props.start },
      version: 3
    }
  }
}

export const blockAdapters = {
  in: {
    heading1: heading1Adapter.in,
    heading2: heading2Adapter.in,
    heading3: heading3Adapter.in,
    paragraph: paragraphAdapter.in,
    bulletList: (block: QuoteBlock) => {
      if (block.version === 3) {
        return bulletListAdapterV3.in(block)
      }

      if (block.version === 2) {
        return bulletListAdapterV2.in(block)
      }

      return bulletListAdapter.in(block)
    },
    phases: phasesAdapter.in,
    numberList: (block: QuoteBlock) => {
      if (block.version === 3) {
        return numberListAdapterV3.in(block)
      }

      if (block.version === 2) {
        return numberListAdapterV2.in(block)
      }

      return numberListAdapter.in(block)
    },
    image: imageAdapter.in,
    coverImage: null,
    pdfCoverImage: null,
    divider: dividerAdapter.in,
    table: tableAdapter.in
  },
  out: {
    heading:
      (ctx: { cubeQueries: CubeDomainInterface['queries'] }) =>
      (block: Extract<typeof blocknoteSchema.Block, { type: 'heading' }>) => {
        /**
         * Heading levels are not strongly typed anymore, and can be configured in
         * `modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks/index.ts`
         */
        return match(block.props.level)
          .with(1, () => heading1Adapter.out(ctx)(block))
          .with(2, () => heading2Adapter.out(ctx)(block))
          .with(3, () => heading3Adapter.out(ctx)(block))
          .otherwise(() => heading3Adapter.out(ctx)(block))
      },
    paragraph: paragraphAdapter.out,
    phases: phasesAdapter.out,
    bulletListItem: bulletListAdapterV3.out,
    numberedListItem: numberListAdapterV3.out,
    image: imageAdapter.out,
    divider: dividerAdapter.out,
    table: tableAdapter.out
  } as Record<
    | 'phases'
    | 'audio'
    | 'bulletListItem'
    | 'checkListItem'
    | 'codeBlock'
    | 'divider'
    | 'file'
    | 'heading'
    | 'image'
    | 'numberedListItem'
    | 'paragraph'
    | 'quote'
    | 'table'
    | 'toggleListItem'
    | 'video',
    (ctx: { cubeQueries: CubeDomainInterface['queries'] }) => (
      block: Extract<
        typeof blocknoteSchema.Block,
        {
          type:
            | 'phases'
            | 'audio'
            | 'bulletListItem'
            | 'checkListItem'
            | 'codeBlock'
            | 'divider'
            | 'file'
            | 'heading'
            | 'image'
            | 'numberedListItem'
            | 'paragraph'
            | 'quote'
            | 'table'
            | 'toggleListItem'
            | 'video'
        }
      >
    ) => any
  >
}
